/**
 * Decision pulse — ambient decision intelligence (not a permanent AI sidebar).
 * Soft≠Live: Live home-context only; path-free action ids; fail-closed seats.
 */
import { useEffect, useState } from "react";
import { html, M, Ico, SPRING, FADE, stop, go } from "./ui.js";
import { path, useAction, useApi, api } from "./api.js";
import { holdTeamRunOrRefuse, continueTeamRunPrefer, takeoverTeamRunPrefer, interveneTeamRunPrefer, continueRefuseToast, holdRefuseToast } from "./hold-team-runtime.js";
import { envelopeSummary, normalizeActionEnvelope } from "./action-envelope.js";
import { operatorSay } from "./operator-say.js";

/** Soft operator labels — on-the-loop (Continue ≠ Approve).
 * Honesty: Allow Team is not painted. id continue is Live; leftover approve still maps.
 */
export function actionLabel(a) {
  const id = String((a && a.id) || "");
  if (id === "continue") return "Continue Team";
  if (id === "approve") return "Continue Team";
  if (id === "hold") return "Hold · Team";
  if (id === "escalate") return "Takeover · escalate";
  if (id === "more_evidence") return "Ask for more evidence";
  return (a && a.label) ? String(a.label) : "Continue";
}

/** Map raw API errors to operator language (never wire paths). */
export function softDecisionErrorOf(err) {
  const n = String((err && err.message) || err || "").trim().toLowerCase();
  if (!n) return "Could not complete that decision.";
  if (/\b403\b|forbidden|insufficient role|ai_incident_forbidden/.test(n)) {
    return "Your account cannot decide on this plan. Ask a manager.";
  }
  if (/\b401\b|session|unauthorized|missing bearer/.test(n)) {
    return "Sign in again to continue.";
  }
  if (/\b409\b|needs_team_run|via_team_runtime/.test(n)) {
    return "Open Mission Room to continue, hold, or take over this plan.";
  }
  if (/\b410\b|retired|gone/.test(n)) {
    return "That leftover control path is closed. Use Mission Room.";
  }
  if (/\b404\b|not_found|not found/.test(n)) {
    return "That plan is no longer waiting.";
  }
  if (/\b503\b|unavailable|disabled/.test(n)) {
    return "Decision service is temporarily offline.";
  }
  if (/network|failed to fetch|timeout/.test(n)) {
    return "Network issue — try again in a moment.";
  }
  if (/\/ai\/|\/ops\/|http|GET |POST /.test(n)) {
    return "Could not complete that decision.";
  }
  return "Could not complete that decision.";
}

/**
 * True only when both Live posture and seat (when provided) allow HITL act.
 * Fail-closed on missing data.
 */
export function canDecisionActOf(seat, decisionPlaneAllowed) {
  if (decisionPlaneAllowed === false) return false;
  if (seat) {
    if (!(seat.hitlAllowed || seat.decisionAllowed)) return false;
  }
  if (decisionPlaneAllowed === true) return true;
  /* Server unknown: seat true opens, no seat → sealed. */
  if (seat && (seat.hitlAllowed || seat.decisionAllowed)) return true;
  return false;
}

/**
 * Shared Live HITL bus for Overview AiDesk + DecisionPulse.
 */
export function useDecisionHome() {
  const ctx = useApi(path("get__ai_home-context"));
  const { busy, envelope } = useAction();
  const [resolved, setResolved] = useState({});
  const [sideEnvelope, setSideEnvelope] = useState(null);

  const data = ctx.data || null;
  const pulse = (data && data.pulse) || "watching";
  const maic = (data && data.maic) || {};
  const waiting = Number(maic.waiting_n || 0) > 0 || pulse === "needs_you";
  const planeFromServer = data == null ? null : data.decision_plane_allowed;
  const actions = ((data && data.decision_actions) || []).filter(
    (a) => a && a.live && !resolved[a.id]
  );
  const why = (data && data.why) || maic.reason_line || "";
  const aiOn = data == null ? true : data.ai_on !== false;
  const honesty = Array.isArray(data && data.honesty) ? data.honesty : [];

  useEffect(() => {
    const ms = waiting ? 8000 : 45000;
    const id = setInterval(() => ctx.reload && ctx.reload(), ms);
    return () => clearInterval(id);
  }, [waiting]);

  const act = async (action, toast) => {
    if (!action) return null;
    if (planeFromServer === false) {
      toast && toast("Only a manager can continue or hold this plan.", "warn");
      return null;
    }
    if (action.id === "hold") {
      const hold = await holdTeamRunOrRefuse(api, path, maic.lead_id, {
        team_run_id: maic.team_run_id || action.team_run_id,
        control_generation: maic.control_generation,
        trigger: "home_context_hold",
      });
      if (!hold.ok) {
        setSideEnvelope(
          normalizeActionEnvelope(hold.out, {
            actor_kind: "Human",
            error: hold.reason || "hold_refused",
          }),
        );
        toast && toast(
          holdRefuseToast(hold.reason),
          "warn",
        );
        return null;
      }
      setSideEnvelope(normalizeActionEnvelope(hold.out || { ok: true }, { actor_kind: "Human" }));
      setResolved((r) => ({ ...r, hold: 1 }));
      toast && toast("Held on Team Runtime", "warn");
      ctx.reload && ctx.reload();
      return hold.out;
    }
    if (action.id === "continue" || action.id === "approve") {
      const cont = await continueTeamRunPrefer(api, path, maic.lead_id, {
        team_run_id: maic.team_run_id || action.team_run_id,
        control_generation: maic.control_generation,
        trigger: "home_context_continue",
      });
      if (cont.ok) {
        setSideEnvelope(normalizeActionEnvelope(cont.out || { ok: true }, { actor_kind: "Human" }));
        setResolved((r) => ({ ...r, continue: 1 }));
        toast && toast("Team continues", "go");
        ctx.reload && ctx.reload();
        return cont.out;
      }
      toast && toast(
        continueRefuseToast(cont.reason),
        "warn",
      );
      return null;
    }
    if (action.id === "escalate") {
      const take = await takeoverTeamRunPrefer(api, path, maic.lead_id, {
        team_run_id: maic.team_run_id || action.team_run_id,
        control_generation: maic.control_generation,
        trigger: "home_context_takeover",
      });
      if (take.ok) {
        setSideEnvelope(normalizeActionEnvelope(take.out || { ok: true }, { actor_kind: "Human" }));
        setResolved((r) => ({ ...r, escalate: 1 }));
        toast && toast("Takeover · Team Runtime", "warn");
        ctx.reload && ctx.reload();
        return take.out;
      }
      toast && toast(
        take.reason === "needs_team_run"
          ? "Takeover needs an open Team Run — open Mission Room first"
          : continueRefuseToast(take.reason),
        "warn",
      );
      return null;
    }
    if (action.id === "more_evidence") {
      const evd = await interveneTeamRunPrefer(api, path, maic.lead_id, {
        team_run_id: maic.team_run_id || action.team_run_id,
        control_generation: maic.control_generation,
        note: "operator requested more evidence",
        facts: ["evidence_requested"],
      });
      if (evd.ok) {
        setSideEnvelope(normalizeActionEnvelope(evd.out || { ok: true }, { actor_kind: "Human" }));
        setResolved((r) => ({ ...r, more_evidence: 1 }));
        toast && toast("Asked for more evidence · Team Runtime", "warn");
        ctx.reload && ctx.reload();
        return evd.out;
      }
      toast && toast(
        evd.reason === "needs_team_run"
          ? "More evidence needs an open Team Run — open Mission Room first"
          : continueRefuseToast(evd.reason),
        "warn",
      );
      return null;
    }
    toast && toast("That action is not available right now", "warn");
    return null;
  };

  return {
    ctx,
    data,
    pulse,
    maic,
    waiting,
    actions,
    busy,
    why,
    aiOn,
    honesty,
    decisionPlaneAllowed: planeFromServer,
    act,
    envelope: envelope || sideEnvelope,
    tonight: (data && data.tonight) || null,
    lamps: (data && data.lamps) || (data && data.tonight && data.tonight.lamps) || null,
    reload: () => ctx.reload && ctx.reload(),
  };
}

export function DecisionPulse({ toast, onFocusDevice, onResolved, seat }) {
  const home = useDecisionHome();
  const { maic, waiting, actions, busy, why, act, decisionPlaneAllowed, envelope } = home;
  const [open, setOpen] = useState(true);
  const deviceId = maic.device_id || "";
  const canAct = canDecisionActOf(seat, decisionPlaneAllowed);

  useEffect(() => {
    if (waiting) setOpen(true);
  }, [waiting, maic.lead_id]);

  if (!waiting) return null;

  const runAct = async (action) => {
    if (!canAct) {
      toast && toast("Only a manager can continue or hold this plan.", "warn");
      return;
    }
    const out = await act(action, toast);
    if (out) onResolved && onResolved(action.id, out);
  };

  return html`<${M.aside} className="decision-pulse" data-open=${open ? "1" : "0"}
      data-cmc-residual="page216-ops-map"
      role="complementary" aria-label="Decision needed"
      initial=${{ y: 24, opacity: 0 }} animate=${{ y: 0, opacity: 1 }}
      exit=${{ y: 16, opacity: 0 }} transition=${SPRING} onClick=${stop}>
    <button type="button" className="decision-pulse__chip" aria-expanded=${open}
        onClick=${() => setOpen((v) => !v)}>
      <span className="decision-pulse__lamp" aria-hidden="true"/>
      <span className="decision-pulse__chip-txt">
        <b>${canAct ? "Needs You · on the loop" : "Waiting for a manager"}</b>
        <em>${maic.waiting_n || 1} waiting</em>
      </span>
      <${Ico} d=${open ? "chevron-down" : "chevron-up"} w=${14}/>
    </button>
    ${open && html`<${M.div} className="decision-pulse__card glass glass--ai"
        initial=${{ opacity: 0, y: 8 }} animate=${{ opacity: 1, y: 0 }}
        exit=${{ opacity: 0, y: 6 }} transition=${FADE}>
      <p className="decision-pulse__why">${operatorSay(why, "A plan is waiting for you.")}</p>
      ${envelope
        ? html`<p className="note" data-cmc-last-action="1">Last action · ${envelopeSummary(envelope) || "—"}</p>`
        : null}
      ${!canAct ? html`<p className="note">You can see the alert. Hold / Takeover need a manager account.</p>` : null}
      ${deviceId && html`<button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => onFocusDevice && onFocusDevice(deviceId)}>
          Show on map</button>`}
      ${maic.lead_id ? html`<button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/response/cases?incident=" + encodeURIComponent(maic.lead_id))}>
          Open Mission Room</button>` : null}
      <div className="decision-pulse__acts">
        ${!canAct
          ? html`<p className="note">Control buttons stay closed for this seat.</p>`
          : actions.length
          ? actions.map((a) => html`
              <button key=${a.id} type="button"
                className=${"btn btn--sm" + (a.id === "continue" || a.id === "approve" ? " btn--go" : "")}
                disabled=${busy} onClick=${() => runAct(a)}>${actionLabel(a)}</button>`)
          : html`<p className="note">Open Mission Room when you are ready to intervene.</p>`}
      </div>
    <//>`}
  <//>`;
}

/** @deprecated Wave28 name — ambient pulse replaced the permanent rail. */
export const AiContextRail = DecisionPulse;
